<template>
  <LegacyModal
    :show-close-button="false"
    @click-close="closeModal"
  >
    <div slot="header">
      <LegacyModalCloseButton @click-close="closeModal" />
    </div>
    <SwipeGesture
      class="carousel-modal-body mb-0"
      @swipe-left="swipeToLeft"
      @swipe-right="swipeToRight"
    >
      <div
        class="text-center"
        :class="{ 'zoom-image': zoom }"
      >
        <img
          v-if="primarySlide.image"
          class="primary-image"
          :src="primarySlide.image"
          :alt="primarySlide.alt_text"
          @click="toggleZoom"
          @keypress="toggleZoom"
        >
        <slot v-else-if="primarySlide.embed_code">
          <div class="embed-content-wrapper">
            <!-- eslint-disable vue/no-v-html -->
            <div
              class="primary-embed"
              v-html="primarySlide.embed_code"
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </slot>
        <div
          v-if="primarySlide.description && !zoom"
          class="primary-description pdp-text mt-4 text-left"
        >
          <p>{{ primarySlide.description }}</p>
        </div>
      </div>
    </SwipeGesture>
    <div
      slot="footer"
      class="d-flex justify-content-center p-2 carousel-modal-footer"
    >
      <div class="d-flex w-100">
        <div
          class="arrow-container d-flex align-items-center mr-2"
          :class="{ 'hide-arrow': hidePrev }"
        >
          <div
            class="arrow prev"
            @click="scrollClick(false)"
            @keypress="scrollClick(false)"
          />
        </div>
        <ul
          ref="scroller"
          class="d-flex"
        >
          <li
            v-for="(item, index) in thumbnails"
            ref="footerThumbnails"
            :key="index"
            class="footer-thumbnails"
            :class="{
              'is-active': index === currentSlide,
              'video-wrap': !item.image && item.embedVideo,
            }"
            @click="changeSlide(index)"
            @keypress="changeSlide(index)"
          >
            <template v-if="item.image">
              <div
                v-if="item.embedVideo"
                class="thumbnail-video-icon"
              >
                <font-awesome-icon
                  class="text-white"
                  icon="play"
                  width="14px"
                  height="14px"
                />
              </div>
              <img
                v-if="item.image"
                :src="item.image"
                :alt="item.alt_text"
                width="120"
                height="70"
              >
            </template>
            <slot v-else>
              <div
                class="click-handler"
              />
              <!-- eslint-disable vue/no-v-html -->
              <div
                class="embed-parent"
                v-html="item.embedVideo"
              />
              <!-- eslint-enable vue/no-v-html -->
            </slot>
          </li>
        </ul>
        <div
          class="arrow-container d-flex align-items-center ml-2"
          :class="{ 'hide-arrow': hideNext }"
        >
          <div
            class="arrow next"
            @click="scrollClick(true)"
            @keypress="scrollClick(true)"
          />
        </div>
      </div>
    </div>
  </LegacyModal>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SwipeGesture from '../../utilities/SwipeGesture.vue';
// TODO:BRO-1276: import DModal from '@appsumo/dorado-nuxt';
import LegacyModal from '../../utilities/LegacyModal.vue';
import LegacyModalCloseButton from '../../utilities/LegacyModalCloseButton.vue';

export default {
  components: {
    SwipeGesture,
    LegacyModal,
    LegacyModalCloseButton,
    FontAwesomeIcon,
  },
  props: {
    slides: {
      type: Array,
      default: () => ([]),
    },
    thumbnails: {
      type: Array,
      required: true,
    },
    initialSlide: {
      type: Number,
      default: 0,
    },
  },
  emits: ['close-modal'],
  data() {
    return {
      zoom: false,
      hidePrev: true,
      hideNext: true,
      currentSlide: this.initialSlide,
    };
  },
  computed: {
    primarySlide() {
      return this.slides[this.currentSlide];
    },
  },
  mounted() {
    const { scroller } = this.$refs;

    if (scroller) {
      scroller.addEventListener('scroll', this.updateScrollButtons);

      // Wait for CSS to apply styles and enable scroll
      setTimeout(() => {
        this.updateScrollButtons();
        this.adjustFooterCarouselView(this.currentSlide);
      }, 500);
    }
  },
  beforeDestroy() {
    const { scroller } = this.$refs;

    if (scroller) {
      scroller.removeEventListener('scroll', this.updateScrollButtons);
    }
  },
  methods: {
    closeModal() {
      this.$emit('close-modal');
    },
    changeSlide(index) {
      this.adjustFooterCarouselView(index);
      this.currentSlide = index;
    },
    toggleZoom() {
      this.zoom = !this.zoom;
    },
    scrollClick(isNext) {
      const { scroller } = this.$refs;

      const amount = isNext ? 100 : -100;

      if (scroller) {
        scroller.scrollBy({ left: amount, behavior: 'smooth' });
      }
    },
    getScrollDistOnSwipe(targetIndex) {
      // jl: returns the distance *from* left that the footer container should scroll
      // when user swipes main image of the carousel modal. goal here is to center the
      // 'active' image in the footer container
      const { scroller, footerThumbnails } = this.$refs;
      const thumbnailImage = footerThumbnails[targetIndex];

      if (!!scroller && !!thumbnailImage) {
        const footerWidth = scroller.clientWidth;
        const imageWidth = thumbnailImage.clientWidth;
        const imageStyle = thumbnailImage.currentStyle || window.getComputedStyle(thumbnailImage);

        if (imageStyle) {
          // jl: browser will always return px string for 'imageStyle.[property]' (i.e. no 'rem')
          const borderRightWidth = Number(imageStyle.borderRightWidth.replace(/px$/, ''));
          const marginRight = Number(imageStyle.marginRight.replace(/px$/, ''));
          const marginLeft = Number(imageStyle.marginLeft.replace(/px$/, ''));

          // jl: scroll dist to align the target image to the left of the viewport
          const alignThumbnailLeftDist = targetIndex * (
            imageWidth + 2 * (
              borderRightWidth + (marginRight || marginLeft)
            )
          );

          // jl: scroll dist to adjust a thumbnail from left to center of viewport
          const centerThumbnailOffset = 0.5 * (footerWidth - imageWidth);

          return alignThumbnailLeftDist - centerThumbnailOffset;
        }
      }

      return -1;
    },
    adjustFooterCarouselView(index) {
      const { scroller } = this.$refs;

      const amount = this.getScrollDistOnSwipe(index);

      if (scroller && amount !== -1) {
        scroller.scrollTo({ left: amount, behavior: 'smooth' });
      }
    },
    updateScrollButtons() {
      const { scroller } = this.$refs;
      const gutter = 12; // images with 6px left and 6px right
      if (!scroller) return;
      const {
        scrollLeft,
        clientWidth,
        scrollWidth,
      } = scroller;

      this.hidePrev = scrollLeft === 0;
      this.hideNext = scrollLeft + clientWidth + gutter >= scrollWidth;
    },
    isSwipeAllowed() {
      // an: Swipe is not possible when zooming on image
      if (this.zoom) {
        return false;
      }

      const mobileMediaQuery = window.matchMedia('(max-width: 767px)');

      // an: Swipe is only possible on mobile
      return mobileMediaQuery.matches;
    },
    swipeToLeft() {
      if (!this.isSwipeAllowed()) {
        return;
      }

      if (this.currentSlide < this.slides.length - 1) {
        this.changeSlide(this.currentSlide + 1);
        return;
      }

      // an: allow user to see first image if swipping last item
      if (this.currentSlide === this.slides.length - 1) {
        this.changeSlide(0);
      }
    },
    swipeToRight() {
      if (!this.isSwipeAllowed()) {
        return;
      }

      if (this.currentSlide > 0) {
        this.changeSlide(this.currentSlide - 1);
        return;
      }

      // an: allow user to see last item if swipping first item
      if (this.currentSlide === 0) {
        this.changeSlide(this.slides.length - 1);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~/styles/dorado/themes/dorado';

  /* stylelint-disable selector-no-qualifying-type */
  .carousel-modal-header ::v-deep .modal-close-button {
    position: relative !important;
    top: initial !important;
    right: initial !important;
    left: initial !important;
    width: 30px !important;
    height: 30px !important;
  }

  .clear-button button.btn.primary.outline {
    padding: 10px 25px;
  }

  .carousel-modal-body {
    overflow-y: auto;
    flex-grow: 1;
    flex-direction: column;
    justify-content: center;
    height: 0; // Safari fix
    height: 100%;
    padding: 1.5rem 1.5rem 0;

    .primary-image {
      border: 1px solid $gray-200;
      box-sizing: border-box;
      border-radius: 4px;
      display: block;
      max-width: 100%;
      max-height: 50vh;
      width: auto;
      height: auto;
      margin-left: auto;
      margin-right: auto;
      cursor: pointer;
    }

    .zoom-image {
      max-height: 66vh;

      .primary-image {
        max-width: initial;
        max-height: initial;
        width: initial;
      }
    }

    /* padding-bottom with % renders based on the parent width, so
        we add a wrapper and explicitly set it here based on the
        viewing height so we don't need many media breakpoints */
    .embed-content-wrapper {
      margin: 0 auto;
      max-width: 90vh;
    }

    .primary-embed {
      position: relative;
      overflow: hidden;
      width: 100%;
      padding-bottom: 56.25%; /* 16:9 Aspect Ratio (divide 9 by 16 = 0.5625) */

      /* stylelint-disable-next-line */
      ::v-deep iframe {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        width: 100%;
        height: 100%;
      }
    }

    .primary-description {
      margin-right: auto;
      margin-left: auto;
      max-width: 700px;
    }
  }

  .footer-thumbnails {
    cursor: pointer;
  }

  .video-wrap {
    position: relative;

    .click-handler {
      z-index: 2;
      width: 100%;
      height: 100%;
      position: absolute;
    }

    .embed-parent {
      z-index: 1;
    }
  }

  .carousel-modal-footer {
    width: 100%;
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    z-index: 1;
    flex-direction: row;

    ul {
      margin: 0;
      padding: 0 0 10px;
      white-space: nowrap;
      width: 100%;
      -webkit-overflow-scrolling: touch; // Allows smooth/flick scrolling
      overflow-x: auto;

      li {
        border: 1px solid $gray-100;
        border-radius: 4px;
        display: inline;
        flex-shrink: 0;
        height: 79px;
        margin: 0 6px;
        overflow: hidden;
        position: relative;
        width: 133px;
        transition: border 0.2s;

        &:first-child {
          margin-left: 0;
        }

        &:last-child {
          margin-right: 0;
        }

        &.is-active {
          border-color: #07c03b;
        }

        &::after {
          background-color: rgba(#1b1b1b, 0.4);
          content: '';
          height: 100%;
          left: 0;
          opacity: 0;
          pointer-events: none;
          position: absolute;
          top: 0;
          transition: opacity 0.2s;
          width: 100%;
        }

        &:not(.is-active):hover::after {
          opacity: 1;
        }

        .thumbnail-video-icon {
          align-items: center;
          background-color: rgba(#1b1b1b, 0.8);
          border-radius: 8px;
          display: flex;
          inset: 50% 0 0 50%;
          height: 30px;
          justify-content: center;
          position: absolute;
          pointer-events: none;
          transform: translate(-50%, -50%);
          width: 40px;
        }

        img {
          width: 100%;
          height: 100%;
        }

        /* stylelint-disable-next-line */
        .embed-parent, .embed-parent ::v-deep iframe {
          height: 100%;
          width: 100%;
        }
      }
    }
  }

  .arrow {
    display: inline-block;
    width: 12px;
    height: 12px;
    background: transparent;
    text-indent: -9999px;
    border-top: 2px solid #434d59;
    border-left: 2px solid #434d59;
    transition: all 150ms ease-in-out;
    text-decoration: none;
    color: transparent;
    margin-bottom: 20px;
  }

  .arrow-container {
    width: 16px;
    height: 100%;
    cursor: pointer;

    &.hide-arrow {
      pointer-events: none;
      opacity: 0;
    }
  }

  .arrow:hover {
    opacity: 0.8;
    border-width: 3px;
  }

  .arrow::before {
    display: block;
    height: 200%;
    width: 200%;
    margin-left: -50%;
    margin-top: -50%;
    content: '';
    transform: rotate(45deg);
  }

  .arrow.prev {
    transform: rotate(-45deg);
    left: 0;
  }

  .arrow.next {
    transform: rotate(135deg);
    right: 0;
  }

  @media (max-height: 720px) {
    .carousel-modal-body {
      .embed-content-wrapper {
        max-width: 60vh;
      }
    }
  }

  @media (max-width: 981px) { // Matches nav mobile breakpoint
    .carousel-modal-body {
      display: flex;
    }
  }
  /* stylelint-enable selector-no-qualifying-type */
</style>
